import { createFileRoute, useNavigate } from "@tanstack/react-router";
import z from "zod";
import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { trpc } from "~/utils/trpc";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@quake/shared-ui/components/ui/card";
import { Input } from "@quake/shared-ui/components/ui/input";
import { Button } from "@quake/shared-ui/components/ui/button";
import { Label } from "@quake/shared-ui/components/ui/label";
import { Alert, AlertDescription } from "@quake/shared-ui/components/ui/alert";
import Loader2Icon from "lucide-react/dist/esm/icons/loader-2";
import MailIcon from "lucide-react/dist/esm/icons/mail";
import AlertCircleIcon from "lucide-react/dist/esm/icons/alert-circle";
import CheckCircle2Icon from "lucide-react/dist/esm/icons/check-circle-2";

export const Route = createFileRoute("/_oauth/oauth/verify-new-account-request")({
  component: RouteComponent,
  validateSearch: (search) => {
    return z.object({ otp: z.number().optional(), email: z.string() }).parse(search);
  },
});

function RouteComponent() {
  const navigate = useNavigate();
  const { otp: urlCode, email } = Route.useSearch();
  const [verificationCode, setVerificationCode] = useState(urlCode || "");
  const [error, setError] = useState<string | null>(null);

  const verifyMutation = useMutation(
    trpc.partnerOauth.verifyNewAccountRequest.mutationOptions({
      onSuccess: (data: any) => {
        if ("redirectPath" in data) {
          navigate({ to: data.redirectPath });
        }
      },
      onError: (error: any) => {
        const errorMessage = error.message || "Verification failed";
        setError(errorMessage);
      },
    }),
  );

  // Auto-verify if code is in URL
  useEffect(() => {
    if (urlCode) {
      verifyMutation.mutate({ verificationCode: String(urlCode) });
    }
  }, [urlCode]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!verificationCode) {
      setError("Please enter your verification code");
      return;
    }

    verifyMutation.mutate({ verificationCode: String(verificationCode) });
  };

  // Show loading state for auto-verification
  if (urlCode && verifyMutation.isPending) {
    return (
      <div className="flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center space-y-4 py-8">
              <Loader2Icon className="h-8 w-8 motion-preset-spin text-primary" />
              <p className="text-sm text-muted-foreground">Verifying your account...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-[320px]">
      <CardHeader className="space-y-2 ">
        <div className="flex items-center justify-center mb-4">
          <div className="bg-primary/10 p-3 rounded-full">
            <MailIcon className="h-6 w-6 text-primary" />
          </div>
        </div>
        <CardTitle className="text-2xl font-bold text-center">Verify Your Account</CardTitle>
        <CardDescription className="text-center">
          {urlCode ? (
            "Processing your verification code..."
          ) : (
            <>
              We've sent a verification code to <strong>{email}</strong>.
              <br />
              Please enter the code below to complete your account setup.
            </>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircleIcon className="h-4 w-4" />
            <AlertDescription>
              {error.includes("expired") && (
                <span>Your verification code has expired. Please request a new one from your administrator.</span>
              )}
              {error.includes("already used") && (
                <span>
                  This verification code has already been used. If you need a new code, please contact your
                  administrator.
                </span>
              )}
              {error.includes("not found") && (
                <span>
                  This verification code was not found. Please check that you entered it correctly or request a new one.
                </span>
              )}
              {!error.includes("expired") && !error.includes("already used") && !error.includes("not found") && error}
            </AlertDescription>
          </Alert>
        )}

        {verifyMutation.isSuccess && (
          <Alert className="mb-4 border-green-200 bg-green-50">
            <CheckCircle2Icon className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              Account verified successfully! Redirecting you to your dashboard...
            </AlertDescription>
          </Alert>
        )}

        {!urlCode && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code">Verification Code</Label>
              <Input
                id="code"
                type="text"
                placeholder="Enter your 5-digit code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                maxLength={5}
                className="text-center text-lg tracking-widest font-mono"
                disabled={verifyMutation.isPending}
                autoFocus
              />
            </div>

            <Button type="submit" className="w-full" disabled={verifyMutation.isPending}>
              {verifyMutation.isPending ? (
                <>
                  <Loader2Icon className="mr-2 h-4 w-4 motion-preset-spin" />
                  Verifying...
                </>
              ) : (
                "Verify Account"
              )}
            </Button>
          </form>
        )}

        <div className="mt-6 text-center text-sm text-muted-foreground">
          <p>Didn't receive a code?</p>
          <p className="mt-1">Check your spam folder or contact your administrator.</p>
        </div>
      </CardContent>
    </div>
  );
}
